import { graphql, PageProps } from "gatsby";
import { withPrismicPreview } from "gatsby-plugin-prismic-previews";
import React from "react";
import { SliceZone, SliceZoneLike } from "@prismicio/react";
import { ThemeProvider, useTheme } from "styled-components";

import { DEFAULT_LANGUAGE } from "@config/consts";

import { AVAILABLE_COUNTRIES, ORIGIN } from "@typings/globals";

import { ErrorFallback } from "@ui/Molecules";
import GlobalStyle from "@ui/style";

import { langFromTag } from "@services/LangFromTag";

import { SmartlookClient } from "@containers/SmartlookClient";

import { useTranslate } from "@hooks/useTranslate";

import { PrismicLandingPageV2Query } from "@www/graphql-types";

import Layout from "../../components/Layout";
import UTMCatcher from "../../components/UTMCatcher";
import SEO, { SeoProps } from "../../components/SEO";

import { landingPageV2Slices } from "./LandingPageV2/LandingPageV2.sliceConfig";
import { CostTableData } from "./LandingPageV2/Slices/CostBreakdown/CostBreakdown.types";

type Props = PageProps<
  PrismicLandingPageV2Query,
  {
    lang: string;
    origin: ORIGIN;
    country: AVAILABLE_COUNTRIES;
    prismicId: string;
  }
>;

const LandingPageV2 = (props: Props) => {
  const translate = useTranslate("batmaid_pwa_generic");
  const language = langFromTag(props.pageContext.lang) || DEFAULT_LANGUAGE;
  const theme = useTheme();

  const seoData: SeoProps = {
    seo_title: {
      text: props.data.prismicLandingPageV2?.data.seo_title.text || "",
    },
    seo_description: {
      text: props.data.prismicLandingPageV2?.data.seo_title.text || "",
    },
    seo_image: props.data.prismicLandingPageV2?.data
      .seo_image as SeoProps["seo_image"],
  };

  if (!props.data.prismicLandingPageV2?.data.body) {
    return (
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ErrorFallback
          somethingWentWrongText={translate("something_went_wrong")}
          appCrashExplanationText={translate("app_crash_explanation")}
        />
      </ThemeProvider>
    );
  }

  const costBreakdownData: CostTableData | null = !props.data
    .prismicLandingPageV2.data.body1
    ? null
    : ({
        slices: props.data.prismicLandingPageV2.data.body1,
        labels:
          props.data.prismicLandingPageV2.data.cost_breakdown_slides_names,
        headers: [
          props.data.prismicLandingPageV2.data
            .cost_breakdown_table_category_header,
          props.data.prismicLandingPageV2.data
            .cost_breakdown_table_percentage_header,
          props.data.prismicLandingPageV2.data.cost_breakdown_table_cost_header,
        ],
      } as CostTableData);

  return (
    <Layout
      isFullWidth
      language={language}
      country={props.pageContext.country}
      origin={ORIGIN.BATMAID}
      backgroundColor={theme.palette.white}
    >
      <SmartlookClient />
      <UTMCatcher />
      <SEO
        withHreflangs
        lang={language}
        title={props.data.prismicLandingPageV2.data.title.text || ""}
        path={props.location.pathname}
        {...seoData}
      />
      <SliceZone
        context={{
          language: language,
          prismicPageId: props.pageContext.prismicId,
          costTableData: costBreakdownData,
        }}
        components={landingPageV2Slices}
        slices={props.data.prismicLandingPageV2.data.body as SliceZoneLike}
      />
    </Layout>
  );
};

export default withPrismicPreview(LandingPageV2);

export const pageQuery = graphql`
  query prismicLandingPageV2($id: String) {
    prismicLandingPageV2(id: { eq: $id }) {
      _previewable
      data {
        title {
          text
        }
        seo_description {
          text
        }
        seo_title {
          text
        }
        seo_image {
          gatsbyImageData(width: 1200)
        }
        body {
          ... on PrismicLandingPageV2DataBodyHero {
            id
            slice_label
            slice_type
            primary {
              hero_title {
                text
              }
              hero_subtitle {
                text
              }
              hero_body {
                richText
              }
              hero_picture {
                alt
                gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                thumbnails {
                  tablet {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  desktop {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  desktopLarge {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                }
              }
            }
          }
          ... on PrismicLandingPageV2DataBodyBenefitslist {
            id
            slice_label
            slice_type
            primary {
              benefitslist_color_mode
              benefitslist_background_color
              benefitslist_title {
                text
              }
            }
            items {
              benefit_icon
              benefit_title {
                text
              }
              benefit_subtitle {
                text
              }
            }
          }
          ... on PrismicLandingPageV2DataBodyCalltoactionbuttons {
            id
            slice_label
            slice_type
            items {
              button_variant
              button_text {
                text
              }
              requires_authorization
              redirect_on_click_url {
                url
                target
                link_type
                slug
              }
            }
          }
          ... on PrismicLandingPageV2DataBodyBenefitexplanation {
            id
            slice_label
            slice_type
            primary {
              inversed
              wide
              explanation_description {
                text
              }
              explanation_title {
                text
              }
              explanation_image {
                alt
                gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                thumbnails {
                  desktop {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  desktopLarge {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  tablet {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  wide_main {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  wide_desktop {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  wide_desktopLarge {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                }
              }
            }
          }
          ... on PrismicLandingPageV2DataBodyFunctionalitydemonstration {
            id
            slice_label
            slice_type
            items {
              first_frame {
                gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
              }
              functionality_description {
                text
              }
              functionality_title {
                text
              }
              second_frame {
                gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED)
              }
            }
          }
          ... on PrismicLandingPageV2DataBodyHerocalltoaction {
            id
            slice_label
            slice_type
            primary {
              hero_background_image {
                alt
                gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                thumbnails {
                  tablet {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  desktop {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                  desktopLarge {
                    gatsbyImageData(layout: FULL_WIDTH, placeholder: BLURRED)
                  }
                }
              }
              hero_description {
                text
              }
              hero_title {
                text
              }
            }
            items {
              button_text {
                text
              }
              button_variant
              redirect_on_click_url {
                url
                slug
                target
                link_type
              }
              requires_authorization
            }
          }
          ... on PrismicLandingPageV2DataBodyTimeline {
            id
            slice_label
            slice_type
            primary {
              timeline_title {
                text
              }
            }
            items {
              event_description {
                richText
              }
              event_title {
                text
              }
            }
          }
          ... on PrismicLandingPageV2DataBodyStartbooking {
            id
            slice_label
            slice_type
            primary {
              startbooking_background_color
              startbooking_title {
                text
              }
              startbooking_button_label {
                text
              }
            }
            items {
              startbooking_service_type
            }
          }
          ... on PrismicLandingPageV2DataBodyCostBreakdown {
            id
            slice_label
            slice_type
            primary {
              cost_breakdown_background
              cost_breakdown_color_mode
              cost_breakdown_title {
                text
              }
              cost_breakdown_description {
                text
              }
            }
          }
        }

        cost_breakdown_table_category_header {
          text
        }
        cost_breakdown_table_percentage_header {
          text
        }
        cost_breakdown_table_cost_header {
          text
        }
        cost_breakdown_slides_names {
          cost_breakdown_slide_id
          cost_breakdown_slide_name {
            text
          }
        }
        body1 {
          ... on PrismicLandingPageV2DataBody1CostBreakdownTableGroup {
            id
            primary {
              cost_breakdown_group_cost {
                text
              }
              cost_breakdown_group_order
              cost_breakdown_group_percentage {
                text
              }
              cost_breakdown_group_slide_id
              cost_breakdown_group_title {
                text
              }
            }
            items {
              cost_breakdown_row_cost {
                text
              }
              cost_breakdown_row_details {
                text
              }
              cost_breakdown_row_heading {
                text
              }
            }
          }
        }
      }
    }
  }
`;
